
import { FCButton } from '@fc/angie-ui';
import ReviewsGrid from '~/components/storyblok/ReviewsGrid/ReviewsGrid.vue';
import { CMSService } from '~/services/storyBlok/cmsService';
import FaqsCollapsibleContainer from '~/components/storyblok/FaqsCollapsibleContainer/FaqsCollapsibleContainer.vue';
import { metaData } from '~/configuration/metaData';
import ShopPackageCards from '~/components/shop/Shop_PackagesCards.vue';
import AtfWhatsIncluded from '~/components/v2/pdps/AtfWhatsIncluded.vue'; // TODO move to more generic location
import packages from '~/data/packages.js';
import { windowMatchMediaMixin } from '~/mixins/matchMedia';

const specs = [
  {
    title: 'Membership',
    copy: `
            <p>An active FightCamp Membership at $39/month is required.</p>
            <p>Membership gives you full access to our complete library of efficient, full-body workouts, and unlimited accounts for your household.</p>
            <p>Why you'll love the FightCamp Membership:</p>
            <ul>
                <li>3000+ on-demand boxing, kickboxing, strength, core & recovery workouts (more added weekly)</li>
                <li>World-class instructors</li>
                <li>Real-time progress tracking</li>
                <li>Programs with day-by-day workout plans, and more!</li>
            </ul>
        `,
  },
  {
    title: 'Console & Product Specs',
    copy: `
            <ul>
                <li>Console Dimensions: 7.9” x 3.9” x 1.3”</li>
                <li>Console Weight: 11 oz</li>
                <li>Water Protection: IP67 rated</li>
                <li>Mounting Options: Magnetic base plate with wall mounting, or place on flat surface</li>
                <li>Power Consumption: 10W</li>
                <li>USB-C Connector for Power: 5V DC @ 2A max</li>
                <li>Connectivity: Ethernet 10/100 Mbps</li>
                <li>Efficiency: Energy Star compliant</li>
                <li>Video Resolution: 1920-1080p at 60fps</li>
                <li>Tracker Battery Capacity: 25mAh</li>
                <li>Tracker Battery Full-Charging Time: 2hrs</li>
                <li>Tracker Battery Duration: 8hrs of workout time on full charge</li>
                <li>Wireless Range: 20-30 ft</li>
                <li>Memory: 4 GB RAM</li>
                <li>Storage: 16 GB internal flash storage</li>
                <li>WiFi 802.11 a/b/g/n/ac - 2.4GHz & 5GHz</li>
            </ul>
        `,
  },
  {
    title: 'Bag Specs',
    copy: `
            <ul>
                <li>Boxing Bag Height: 67" tall, when fully assembled</li>
                <li>Boxing Bag Top: 18" diameter</li>
                <li>Boxing Bag Material: Filled with high-density, durable foam, vinyl cover</li>
                <li>Boxing Bag Assembly: Hook-and-loop tension belts secure top of the bag to the base</li>
                <li>Boxing Bag Base: 24" diameter</li>
                <li>Boxing Bag Weight: Unfilled base (13lbs), Base filled with water (250lbs), Base filled with sand (350lbs), Bag top (17lbs), Fully assembled bag with sand (380lbs)</li>
                <li>Mobility: Tilt bag and roll to its desired location, even when fully filled & assembled</li>
            </ul>
        `,
  },
  {
    title: 'Warranty & 30-Day Refunds',
    copy: `
      <p class="fc-bold">Warranty<p>
      <p>Limited Warranty - 12-month plan included with your package, covers lost or damaged Trackers</p>

      <p class="fc-bold">30-day Refund Policy</p>
      <p>Try FightCamp at home for 30 days. If it’s not for you, we’ll give you a full refund.*</p>

      <p>*Terms and conditions apply. <a href="/return-policy">See Full Return Policy</a></p>
    `,
  },
  {
    title: 'Delivery & Setup',
    copy: `
      <p>Please note the boxing bag is shipped separately from the Console and accessories, and the boxing bag will arrive in 2 boxes: one for the base, one for the top.</p>

      <p>There may be a slight delay between the arrival of these separate shipments.</p>

      <p class="fc-bold">Setup</p>
      <p>Quickly connect your Console to your TV via HDMI, or connect to your mobile device via Bluetooth. Fill your boxing bag with sand or water, as preferred (sand not included), then simply attach the top to the base.</p>

      <a href="/how-it-works">Read More Setup Details</a>

      <p class="fc-bold">Setup Documents</p>
      <a href="https://joinfightcamp.com/bag-setup" target="_blank">Download Bag Setup Instructions</a>
      <br>
      <a href="https://joinfightcamp.com/console-setup" target="_blank">Download Console Setup Instructions</a>
    `,
  },
];

export default {
  name: 'Shop',
  mixins: [windowMatchMediaMixin],
  components: {
    ReviewsGrid,
    FaqsCollapsibleContainer,
    ShopPackageCards,
    AtfWhatsIncluded,
    FCButton,
  },
  data() {
    return {
      reviews: [],
      faqs: [],
      packages,
      specs,
      expandedCardIndexes: [],
      expandedSpecIndex: null,
    };
  },
  head: {
    ...metaData.shop,
  },
  async asyncData({ $storyapi }) {
    try {
      const storyBlok = new CMSService({ cmsService: $storyapi });
      const reviews = await storyBlok.getAll('user-reviews', { sort_by: 'content.reviewDate:desc' });

      const faqs = await storyBlok.get('global/globalpackagefaqs');
      const faqList = faqs?.data?.story?.content?.global?.[0]?.container || [];

      return { reviews, faqs: faqList };
    } catch (e) {
      console.log(e);
    }
    return [];
  },
  mounted() {
    if (!this.mixinMatchMedia.isTabletUp) {
      this.expandedCardIndexes = [1];
    } else {
      this.expandedCardIndexes = this.packages.map((_, i) => i);
    }
  },
  // TODO : add a watcher for matchMedia changes to update expandedCardIndexes
  methods: {
    onToggleCard(idx) {
      this.expandedCardIndexes = this.toggleCard(idx, this.expandedCardIndexes);
    },
    toggleCard(idx, expandedCardIndexes) {
      if (this.mixinMatchMedia.isTabletUp) {
        return this.packages.map((_, i) => i);
      }
      const isExpanded = expandedCardIndexes.includes(idx);

      return isExpanded
        ? expandedCardIndexes.filter(i => i !== idx)
        : [...expandedCardIndexes, idx];
    },
    scrollToTop() {
      if (process.client) {
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }
    },
    toggleSpec(idx) {
      this.expandedSpecIndex = this.expandedSpecIndex === idx ? null : idx;
    },
  },
};
